import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { BookOpen, Bot, ChevronRight, FileText, Sparkles, X } from "lucide-react";
import { Overlay } from "./UI";
import { getDpdpPolicies } from "../../data/dpdpPolicies";

export const GAP_POLICY_BLINK_CSS = `
@keyframes gap-policy-pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(20, 184, 166, 0.45);
    border-color: rgba(45, 212, 191, 0.5);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(20, 184, 166, 0.14);
    border-color: rgba(45, 212, 191, 0.85);
  }
}
@keyframes gap-policy-dot-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.45; transform: scale(1.4); }
}
@keyframes gap-policy-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-7px); }
}
@keyframes gap-policy-glow-teal {
  0%, 100% {
    box-shadow:
      0 0 0 3px rgba(255, 255, 255, 0.95),
      0 8px 24px rgba(13, 148, 136, 0.42),
      0 0 28px rgba(6, 182, 212, 0.22);
  }
  50% {
    box-shadow:
      0 0 0 3px rgba(255, 255, 255, 1),
      0 12px 32px rgba(13, 148, 136, 0.55),
      0 0 40px rgba(6, 182, 212, 0.32);
  }
}
@keyframes gap-policy-ring-pulse {
  0% { transform: scale(0.92); opacity: 0.55; }
  70% { transform: scale(1.35); opacity: 0; }
  100% { transform: scale(1.35); opacity: 0; }
}
@keyframes gap-policy-sparkle {
  0%, 88%, 100% { opacity: 0; transform: scale(0.4) rotate(0deg); }
  90% { opacity: 1; transform: scale(1) rotate(12deg); }
  93% { opacity: 0.85; transform: scale(0.85) rotate(-8deg); }
  96% { opacity: 0; transform: scale(0.5) rotate(4deg); }
}
@keyframes gap-policy-sparkle-delayed {
  0%, 93%, 100% { opacity: 0; transform: scale(0.3); }
  95% { opacity: 0.9; transform: scale(0.9); }
  98% { opacity: 0; transform: scale(0.4); }
}
@keyframes gap-policy-shimmer {
  0% { background-position: 200% center; }
  100% { background-position: -200% center; }
}
.gap-policy-rec-blink {
  animation: gap-policy-pulse 2.2s ease-in-out infinite;
}
.gap-policy-rec-dot {
  animation: gap-policy-dot-pulse 1.5s ease-in-out infinite;
}
.gap-policy-float {
  animation: gap-policy-float 3.4s ease-in-out infinite;
}
.gap-policy-glow-teal {
  animation: gap-policy-glow-teal 2.6s ease-in-out infinite;
}
.gap-policy-ring-pulse {
  animation: gap-policy-ring-pulse 2.4s ease-out infinite;
}
.gap-policy-sparkle-orbit {
  animation: gap-policy-sparkle 4.5s ease-in-out infinite;
}
.gap-policy-sparkle-orbit-delayed {
  animation: gap-policy-sparkle-delayed 5.2s ease-in-out infinite;
}
.gap-policy-disclosure-body {
  animation: gap-policy-disclosure-in 0.22s ease-out;
}
.gap-policy-copilot-pill {
  cursor: grab;
  position: relative;
  width: 3.75rem;
  height: 3.75rem;
  overflow: visible;
}
@keyframes gap-policy-agent-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}
@keyframes gap-policy-agent-halo {
  0%, 100% { opacity: 0.35; transform: scale(1); }
  50% { opacity: 0.65; transform: scale(1.06); }
}
@keyframes gap-policy-agent-shine {
  0% { transform: translateX(-130%) skewX(-12deg); opacity: 0; }
  35% { opacity: 0.55; }
  100% { transform: translateX(130%) skewX(-12deg); opacity: 0; }
}
@keyframes gap-policy-badge-pop {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.12); }
}
.gap-policy-agent-halo {
  position: absolute;
  inset: -7px;
  border-radius: 9999px;
  background: conic-gradient(from 210deg, #2dd4bf, #06b6d4, #34d399, #14b8a6, #2dd4bf);
  opacity: 0.5;
  animation: gap-policy-agent-halo 3.2s ease-in-out infinite;
  z-index: 0;
  filter: blur(1px);
}
.gap-policy-agent-circle {
  position: relative;
  z-index: 1;
  width: 3.75rem;
  height: 3.75rem;
  border-radius: 9999px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(145deg, #0f766e 0%, #0d9488 28%, #0891b2 58%, #059669 100%);
  box-shadow:
    0 0 0 2px rgba(255, 255, 255, 0.92),
    0 10px 28px rgba(13, 148, 136, 0.45),
    0 2px 8px rgba(6, 182, 212, 0.25),
    inset 0 1px 1px rgba(255, 255, 255, 0.35);
  transition: transform 0.28s cubic-bezier(0.34, 1.4, 0.64, 1), box-shadow 0.28s ease;
  animation: gap-policy-agent-float 3.8s ease-in-out infinite;
  overflow: hidden;
}
.gap-policy-agent-shine {
  position: absolute;
  inset: 0;
  background: linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.42) 50%, transparent 65%);
  animation: gap-policy-agent-shine 4.5s ease-in-out infinite;
  pointer-events: none;
  z-index: 2;
}
.gap-policy-copilot-pill:hover .gap-policy-agent-circle,
.gap-policy-copilot-pill:focus-visible .gap-policy-agent-circle {
  animation: none;
  transform: scale(1.07);
  box-shadow:
    0 0 0 3px rgba(255, 255, 255, 0.98),
    0 14px 36px rgba(13, 148, 136, 0.52),
    0 4px 12px rgba(6, 182, 212, 0.3),
    inset 0 1px 1px rgba(255, 255, 255, 0.4);
}
.gap-policy-agent-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 1.5rem;
  height: 1.5rem;
  padding: 0 0.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: linear-gradient(135deg, #fcd34d 0%, #f97316 55%, #ea580c 100%);
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  line-height: 1;
  box-shadow: 0 0 0 2.5px #fff, 0 4px 12px rgba(234, 88, 12, 0.45);
  z-index: 30;
  pointer-events: none;
}
.gap-policy-orb-attention .gap-policy-agent-badge {
  animation: gap-policy-badge-pop 0.6s ease-in-out 2;
}
.gap-policy-agent-circle-wrap {
  position: relative;
  width: 3.75rem;
  height: 3.75rem;
  overflow: visible;
}
.gap-policy-dragging .gap-policy-copilot-pill {
  cursor: grabbing;
}
.gap-policy-dragging .gap-policy-agent-circle {
  animation: none;
}
.gap-policy-agent-label {
  position: absolute;
  right: calc(100% + 14px);
  top: 50%;
  transform: translateY(-50%) translateX(10px) scale(0.96);
  height: 3.75rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.625rem;
  padding: 0 1.1rem 0 0.85rem;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  background: linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(236,253,245,0.96) 100%);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border: 1px solid rgba(45, 212, 191, 0.55);
  border-radius: 9999px;
  box-shadow:
    0 12px 32px rgba(13, 148, 136, 0.16),
    0 2px 8px rgba(6, 182, 212, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  transition: opacity 0.26s ease, visibility 0.26s ease, transform 0.32s cubic-bezier(0.34, 1.4, 0.64, 1);
}
.gap-policy-agent-label-icon {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 9999px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(145deg, #0d9488, #06b6d4);
  box-shadow: 0 4px 10px rgba(13, 148, 136, 0.3);
  color: #fff;
}
.gap-policy-agent-label-sub {
  font-size: 10px;
  color: #0f766e;
  margin-top: 2px;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.gap-policy-copilot-pill:hover .gap-policy-agent-label,
.gap-policy-copilot-pill:focus-visible .gap-policy-agent-label {
  opacity: 1;
  visibility: visible;
  transform: translateY(-50%) translateX(0) scale(1);
}
.gap-policy-agent-hint {
  position: absolute;
  right: 0;
  bottom: calc(100% + 12px);
  white-space: nowrap;
  font-size: 10px;
  font-weight: 600;
  color: #115e59;
  padding: 0.35rem 0.75rem;
  border-radius: 9999px;
  background: linear-gradient(135deg, rgba(255,255,255,0.98), rgba(240,253,250,0.95));
  border: 1px solid rgba(45, 212, 191, 0.45);
  box-shadow: 0 8px 20px rgba(13, 148, 136, 0.12);
  pointer-events: none;
}
.gap-policy-agent-dot {
  display: none;
}
.gap-policy-copilot-shimmer {
  display: none;
}
@keyframes gap-policy-disclosure-in {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes gap-policy-enter {
  0% {
    opacity: 0;
    transform: translate(-14px, 18px) scale(0.88);
  }
  60% {
    opacity: 1;
    transform: translate(2px, -2px) scale(1.02);
  }
  100% {
    opacity: 1;
    transform: translate(0, 0) scale(1);
  }
}
.gap-policy-enter {
  animation: gap-policy-enter 0.65s cubic-bezier(0.34, 1.4, 0.64, 1) forwards;
}
@keyframes gap-policy-orb-nudge {
  0%, 100% { transform: scale(1); }
  40% { transform: scale(1.1); }
}
.gap-policy-orb-attention .gap-policy-agent-circle {
  animation: gap-policy-orb-nudge 0.55s ease-in-out 2;
}
.gap-policy-dragging {
  cursor: grabbing !important;
  user-select: none;
}
`;

function PolicyAssistantOrb({ size = "sm", prominent = false }) {
  const sizes = {
    sm: { wrap: "w-9 h-9", round: "rounded-xl", icon: 17, stroke: 2.5 },
    lg: { wrap: "w-12 h-12", round: "rounded-2xl", icon: 24, stroke: 2.5 },
    compact: { wrap: "w-11 h-11", round: "rounded-2xl", icon: 22, stroke: 2.65 },
  };
  const s = sizes[size] || sizes.sm;
  const shell = prominent ? "w-12 h-12" : s.wrap;

  return (
    <div className={`relative flex-shrink-0 ${shell}`} aria-hidden>
      {prominent && (
        <>
          <span className="gap-policy-sparkle-orbit absolute -top-1 right-0 text-amber-400 pointer-events-none z-20">
            <Sparkles size={13} strokeWidth={2.5} fill="currentColor" fillOpacity={0.35} />
          </span>
          <span className="gap-policy-sparkle-orbit-delayed absolute -bottom-1 -left-1 text-cyan-300 pointer-events-none z-20">
            <Sparkles size={10} strokeWidth={2.5} fill="currentColor" fillOpacity={0.3} />
          </span>
          <span className="gap-policy-ring-pulse absolute inset-0 rounded-2xl border-2 border-teal-400/50 pointer-events-none" />
        </>
      )}
      <div
        className={`gap-policy-orb-core gap-policy-float gap-policy-glow-teal ${s.wrap} ${s.round} flex items-center justify-center bg-gradient-to-br from-teal-600 via-cyan-500 to-emerald-500 ring-[2.5px] ring-white`}
      >
        <Bot
          size={s.icon}
          className="text-white"
          strokeWidth={s.stroke}
          fill="white"
          fillOpacity={0.15}
          style={{ filter: "drop-shadow(0 2px 5px rgba(0,0,0,0.25))" }}
        />
      </div>
    </div>
  );
}

function PolicyRecommendationsBody({
  recommendations,
  onViewPolicy,
  onGenerate,
  variant = "main",
}) {
  const count = recommendations.length;
      const policyIds = recommendations.map((r) => r.policy.id).filter(Boolean);

  if (count === 0) {
    return (
      <p className="text-sm text-slate-600 leading-relaxed">
        Answer <span className="font-semibold text-slate-800">No</span> or{" "}
        <span className="font-semibold text-slate-800">Partially</span> on gap questions and
        matching DPDP policies will appear here. Generate or open them in Policy Store without
        leaving OneDPDP.
      </p>
    );
  }

  return (
    <>
      <p className="text-sm text-slate-600 mb-4 leading-relaxed">
        {count} DPDP-aligned polic{count === 1 ? "y" : "ies"} matched to your gap answers.
      </p>
      <div className="space-y-0">
        {recommendations.map((item) => (
          <PolicyRecItem
            key={item.policy.id}
            item={item}
            variant={variant}
            onViewPolicy={onViewPolicy}
            onGenerate={onGenerate}
          />
        ))}
      </div>
      {count > 1 && (
        <button
          type="button"
          onClick={() => onGenerate?.(policyIds)}
          className="w-full mt-2 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-teal-600 via-cyan-600 to-emerald-600 hover:from-teal-700 hover:via-cyan-700 hover:to-emerald-700 transition-colors shadow-md shadow-teal-500/25"
        >
          <Sparkles size={15} />
          Generate all {count} policies
          <ChevronRight size={15} />
        </button>
      )}
    </>
  );
}

function PolicyRecItem({ item, variant = "main", onViewPolicy, onGenerate }) {
  const { policy, gapQuestions } = item;
  const isMain = variant === "main";

  return (
    <div
      className={`rounded-xl border border-teal-200/80 bg-gradient-to-br from-teal-50/95 via-cyan-50/60 to-emerald-50/50 ${
        isMain ? "p-4" : "p-3"
      }`}
      style={{ marginBottom: isMain ? 12 : 8 }}
    >
      <div className="flex items-start gap-3 min-w-0">
        <div
          className={`${isMain ? "w-10 h-10" : "w-8 h-8"} rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-teal-600 via-cyan-500 to-emerald-500`}
          style={{ boxShadow: "0 4px 12px rgba(13,148,136,0.35)" }}
        >
          <FileText size={isMain ? 16 : 14} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className={`font-bold text-slate-900 leading-snug ${
              isMain ? "text-sm" : "text-[11px] line-clamp-2"
            }`}
          >
            {policy.title}
          </p>
          <p className={`text-slate-600 mt-1 leading-relaxed ${isMain ? "text-xs" : "text-[10px] line-clamp-1"}`}>
            {policy.subtitle}
          </p>
          <span
            className={`inline-flex mt-2 px-2.5 py-0.5 rounded-full font-semibold bg-white/90 text-teal-800 border border-teal-100 ${
              isMain ? "text-[11px]" : "text-[9px]"
            }`}
          >
            {policy.actRef}
          </span>
          {gapQuestions.length > 0 && (
            <p className={`text-slate-500 mt-2 ${isMain ? "text-xs" : "text-[9px]"}`}>
              Closes {gapQuestions.length} gap{gapQuestions.length !== 1 ? "s" : ""} in your answers
            </p>
          )}
          {isMain && gapQuestions.length > 0 && (
            <ul className="mt-2 space-y-1 pl-0 list-none">
              {gapQuestions.slice(0, 3).map((g) => (
                <li key={g.id} className="text-[11px] text-slate-500 flex items-start gap-1.5">
                  <span className="font-mono text-[10px] text-teal-700 font-semibold shrink-0">{g.id}</span>
                  <span className="line-clamp-2">{g.text}</span>
                </li>
              ))}
              {gapQuestions.length > 3 && (
                <li className="text-[10px] text-slate-400 pl-0">
                  +{gapQuestions.length - 3} more gap{gapQuestions.length - 3 !== 1 ? "s" : ""}
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
      <div className={`flex flex-wrap gap-2 ${isMain ? "mt-3" : "mt-2.5"}`}>
        <button
          type="button"
          onClick={() => onViewPolicy?.(policy)}
          className={`flex items-center gap-1.5 rounded-lg font-bold text-teal-800 bg-white border border-teal-200 hover:bg-teal-50 transition-colors ${
            isMain ? "px-3 py-1.5 text-xs" : "px-2.5 py-1 text-[10px]"
          }`}
        >
          <BookOpen size={isMain ? 13 : 11} />
          Policy Store
        </button>
        {policy.id && (
        <button
          type="button"
          onClick={() => onGenerate?.([policy.id])}
          className={`flex items-center gap-1.5 rounded-lg font-bold text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 transition-colors ${
            isMain ? "px-3 py-1.5 text-xs" : "px-2.5 py-1 text-[10px]"
          }`}
        >
          <Sparkles size={isMain ? 13 : 11} />
          Generate
        </button>
        )}
      </div>
    </div>
  );
}

const COPILOT_POS_KEY = "gap-policy-agent-position-v5";
const POLICY_AGENT_LABEL = "Policy Agent";
const DRAG_THRESHOLD = 6;
const AGENT_CIRCLE_SIZE = 60;
const AGENT_EDGE_PAD = 16;
const AGENT_BADGE_OVERFLOW = 12;
const AGENT_LABEL_WIDTH = 220;
const LAYOUT_HEADER = 52;
const LAYOUT_FOOTER = 40;
const GAP_NAV_CLEARANCE = 80;

function getAgentFootprint() {
  return {
    width: AGENT_CIRCLE_SIZE + AGENT_BADGE_OVERFLOW * 2,
    height: AGENT_CIRCLE_SIZE + AGENT_BADGE_OVERFLOW * 2,
  };
}

function getSidebarInset() {
  if (typeof window === "undefined") return 0;
  return window.innerWidth >= 901 ? 284 : 0;
}

function getViewportSafeBounds(width = 0, height = 0) {
  const sidebar = getSidebarInset();
  const margin = AGENT_EDGE_PAD;
  const badge = AGENT_BADGE_OVERFLOW;
  return {
    minX: sidebar + margin + AGENT_LABEL_WIDTH,
    minY: LAYOUT_HEADER + margin + badge,
    maxX: window.innerWidth - margin - badge - width,
    maxY: window.innerHeight - LAYOUT_FOOTER - GAP_NAV_CLEARANCE - margin - badge - height,
  };
}

function clampCopilotPosition(x, y, width, height) {
  const b = getViewportSafeBounds(width, height);
  return {
    x: Math.min(Math.max(b.minX, x), Math.max(b.minX, b.maxX)),
    y: Math.min(Math.max(b.minY, y), Math.max(b.minY, b.maxY)),
  };
}

function getDefaultCopilotPosition() {
  const { width, height } = getAgentFootprint();
  const b = getViewportSafeBounds(width, height);
  return { x: b.maxX, y: b.maxY };
}

function getInitialCopilotPosition() {
  if (typeof window === "undefined") return null;
  const saved = readSavedCopilotPosition();
  const { width, height } = getAgentFootprint();
  return saved
    ? clampCopilotPosition(saved.x, saved.y, width, height)
    : getDefaultCopilotPosition();
}

function getAgentStyle(position) {
  const { width, height } = getAgentFootprint();
  const marginR = AGENT_EDGE_PAD + AGENT_BADGE_OVERFLOW;
  const marginB = LAYOUT_FOOTER + GAP_NAV_CLEARANCE + AGENT_BADGE_OVERFLOW;

  if (!position) {
    return { right: marginR, bottom: marginB, left: "auto", top: "auto" };
  }

  return {
    right: Math.max(marginR, window.innerWidth - position.x - width),
    bottom: Math.max(marginB, window.innerHeight - position.y - height),
    left: "auto",
    top: "auto",
  };
}

function readSavedCopilotPosition() {
  try {
    const raw = sessionStorage.getItem(COPILOT_POS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x === "number" && typeof parsed?.y === "number") return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

function useDraggableCopilotPosition(visible, onTap) {
  const nodeRef = useRef(null);
  const onTapRef = useRef(onTap);
  onTapRef.current = onTap;
  const [position, setPosition] = useState(getInitialCopilotPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [showDragHint, setShowDragHint] = useState(false);
  const dragRef = useRef({ active: false, moved: false, startX: 0, startY: 0, origX: 0, origY: 0 });
  const latestPosRef = useRef(null);

  const measureAndPlace = useCallback(() => {
    const { width, height } = getAgentFootprint();
    const saved = readSavedCopilotPosition();
    setPosition(saved ? clampCopilotPosition(saved.x, saved.y, width, height) : getDefaultCopilotPosition());
  }, []);

  useLayoutEffect(() => {
    if (!visible) return;
    measureAndPlace();
  }, [visible, measureAndPlace]);

  useEffect(() => {
    if (!visible) return undefined;
    const onResize = () => {
      const { width, height } = getAgentFootprint();
      setPosition((prev) =>
        prev ? clampCopilotPosition(prev.x, prev.y, width, height) : getDefaultCopilotPosition(),
      );
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;
    setShowDragHint(true);
    const t = setTimeout(() => setShowDragHint(false), 4500);
    return () => clearTimeout(t);
  }, [visible]);

  const onPointerDown = useCallback(
    (e) => {
      if (e.button !== 0 || !position) return;
      e.preventDefault();
      dragRef.current = {
        active: true,
        moved: false,
        startX: e.clientX,
        startY: e.clientY,
        origX: position.x,
        origY: position.y,
      };
      setIsDragging(true);

      const onMove = (ev) => {
        const d = dragRef.current;
        if (!d.active) return;
        const dx = ev.clientX - d.startX;
        const dy = ev.clientY - d.startY;
        if (!d.moved && Math.hypot(dx, dy) >= DRAG_THRESHOLD) d.moved = true;
        if (d.moved) {
          const { width, height } = getAgentFootprint();
          const next = clampCopilotPosition(d.origX + dx, d.origY + dy, width, height);
          latestPosRef.current = next;
          setPosition(next);
        }
      };

      const onUp = () => {
        const d = dragRef.current;
        const didMove = d.moved;
        d.active = false;
        d.moved = false;
        setIsDragging(false);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        if (didMove && latestPosRef.current) {
          try {
            sessionStorage.setItem(COPILOT_POS_KEY, JSON.stringify(latestPosRef.current));
          } catch {
            /* ignore */
          }
        } else if (!didMove) {
          onTapRef.current?.();
        }
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [position],
  );

  return { nodeRef, position, isDragging, showDragHint, onPointerDown };
}

/** Floating AI assistant — appears when gaps are found; draggable so it never blocks the form. */
export function GapPolicyFloatingAssistant({
  id = "gap-policy-floating-assistant",
  recommendations = [],
  onViewPolicy,
  onGenerate,
}) {
  const [open, setOpen] = useState(false);
  const [justUpdated, setJustUpdated] = useState(false);
  const [entered, setEntered] = useState(false);
  const prevCountRef = useRef(0);
  const count = recommendations.length;
  const visible = count > 0;

  const { nodeRef, position, isDragging, showDragHint, onPointerDown } =
    useDraggableCopilotPosition(visible, () => setOpen(true));

  useEffect(() => {
    const prev = prevCountRef.current;
    if (count > 0 && prev === 0) setEntered(true);
    if (count > prev) {
      setJustUpdated(true);
      const t = setTimeout(() => setJustUpdated(false), 3200);
      prevCountRef.current = count;
      return () => clearTimeout(t);
    }
    prevCountRef.current = count;
    return undefined;
  }, [count]);

  if (!visible) return null;

  const agentUi = (
    <>
      <div
        ref={nodeRef}
        id={id}
        data-tour-id="tour-gap-policy-panel"
        className={[
          "fixed z-[200] touch-none select-none overflow-visible",
          entered ? "gap-policy-enter" : "",
          isDragging ? "gap-policy-dragging" : "",
        ].join(" ")}
        style={getAgentStyle(position)}
        role="region"
        aria-label={`${POLICY_AGENT_LABEL} — ${count} recommendation${count !== 1 ? "s" : ""}`}
      >
        {showDragHint && !isDragging && (
          <p className="gap-policy-agent-hint">
            Drag · Tap to open
          </p>
        )}

        <div
          role="button"
          tabIndex={0}
          onPointerDown={onPointerDown}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpen(true);
            }
          }}
          className={`group gap-policy-copilot-pill focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-400/80 focus-visible:ring-offset-2 focus-visible:rounded-full active:scale-[0.97] transition-transform duration-200 ${justUpdated ? "gap-policy-orb-attention" : ""}`}
          aria-label={`${POLICY_AGENT_LABEL} — ${count} recommendation${count !== 1 ? "s" : ""}. Drag to move, tap to open.`}
          title={`${POLICY_AGENT_LABEL} — ${count} recommendation${count !== 1 ? "s" : ""}`}
        >
          <div className="gap-policy-agent-label pointer-events-none">
            <span className="gap-policy-agent-label-icon" aria-hidden>
              <Sparkles size={11} strokeWidth={2.5} />
            </span>
            <div>
              <p className="text-xs font-bold text-teal-950 leading-tight tracking-tight">
                {POLICY_AGENT_LABEL}
              </p>
              <p className="gap-policy-agent-label-sub">
                {count} policy tip{count !== 1 ? "s" : ""} for your gaps
              </p>
            </div>
          </div>
          <div className="gap-policy-agent-circle-wrap pointer-events-none">
            <span className="gap-policy-agent-halo" aria-hidden />
            <div className="gap-policy-agent-circle">
              <span className="gap-policy-agent-shine" aria-hidden />
              <Bot
                size={26}
                className="text-white relative z-[3]"
                strokeWidth={2.35}
                fill="white"
                fillOpacity={0.14}
                style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.22))" }}
              />
            </div>
            <span className="gap-policy-agent-badge" aria-hidden>
              {count}
            </span>
          </div>
        </div>
      </div>

      <Overlay open={open} onClose={() => setOpen(false)} zIndex={250}>
        <div
          className="fixed inset-0 flex items-end sm:items-center justify-center p-4 pointer-events-none"
          role="presentation"
        >
          <div
            className="pointer-events-auto w-full max-w-lg max-h-[min(85vh,640px)] flex flex-col rounded-2xl border border-teal-200/80 bg-white shadow-2xl shadow-teal-500/15 gap-policy-disclosure-body overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="gap-policy-modal-title"
          >
            <div className="flex items-start gap-3 px-5 py-4 border-b border-teal-100/90 bg-gradient-to-br from-teal-50 via-cyan-50/80 to-emerald-50/60 shrink-0">
              <PolicyAssistantOrb size="lg" prominent />
              <div className="flex-1 min-w-0 pt-0.5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-teal-600/90 mb-0.5">
                  AI Assistant
                </p>
                <h2 id="gap-policy-modal-title" className="text-base font-bold text-teal-950">
                  {POLICY_AGENT_LABEL}
                </h2>
                <p className="text-xs text-teal-800/75 mt-0.5 leading-relaxed">
                  {count > 0
                    ? `${count} recommendation${count !== 1 ? "s" : ""} for your gaps`
                    : "Policies appear when you mark No or Partially"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 scrollbar-thin">
              <PolicyRecommendationsBody
                recommendations={recommendations}
                onViewPolicy={(policyId) => {
                  setOpen(false);
                  onViewPolicy?.(policyId);
                }}
                onGenerate={(policyIds) => {
                  setOpen(false);
                  onGenerate?.(policyIds);
                }}
                variant="main"
              />
            </div>
          </div>
        </div>
      </Overlay>
    </>
  );

  if (typeof document === "undefined") return null;
  return createPortal(agentUi, document.body);
}

/** Primary recommendations panel — used on results screen. */
export function GapPolicyMainPanel({
  id = "gap-policy-recommendations",
  recommendations = [],
  onViewPolicy,
  onGenerate,
}) {
  const count = recommendations.length;

  if (!count) return null;

  return (
    <div
      id={id}
      className="rounded-2xl border border-teal-200/70 bg-gradient-to-b from-teal-50/80 via-cyan-50/30 to-white shadow-sm p-5"
    >
      <div className="flex items-start gap-3 mb-4">
        <PolicyAssistantOrb size="lg" prominent />
        <div className="min-w-0">
          <h3 className="text-base font-bold text-teal-950">Policies to close your gaps</h3>
          <p className="text-sm text-teal-800/75 mt-1 leading-relaxed">
            {count} DPDP-aligned polic{count === 1 ? "y" : "ies"} matched to your gap answers.
          </p>
        </div>
      </div>
      <PolicyRecommendationsBody
        recommendations={recommendations}
        onViewPolicy={onViewPolicy}
        onGenerate={onGenerate}
        variant="main"
      />
    </div>
  );
}

/** Inline hint under a gap question. */
export function GapQuestionPolicyHint({ policyIds, organizationName, onViewPolicy, onGenerate }) {
  if (!policyIds?.length) return null;

  const policies = getDpdpPolicies({ organizationName }).filter((p) => policyIds.includes(p.id));
  if (!policies.length) return null;

  return (
    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3.5 gap-policy-rec-blink">
      <p className="text-xs font-bold text-amber-900 flex items-center gap-2 mb-2.5">
        <span className="gap-policy-rec-dot w-2 h-2 rounded-full bg-amber-500 inline-block" />
        Recommended policy to close this gap
      </p>
      <div className="space-y-2">
        {policies.map((policy) => (
          <div
            key={policy.id}
            className="rounded-xl bg-white border border-amber-100 px-3 py-2.5"
          >
            <p className="text-sm font-semibold text-slate-900">{policy.title}</p>
            <p className="text-xs text-slate-500 mt-0.5">{policy.subtitle}</p>
            <p className="text-[11px] text-indigo-600 font-medium mt-1">{policy.actRef}</p>
            <div className="flex gap-2 mt-2.5">
              <button
                type="button"
                onClick={() => onViewPolicy?.(policy)}
                className="px-3 py-1.5 rounded-lg text-xs font-bold text-indigo-700 bg-indigo-50 border border-indigo-100"
              >
                View in Policy Store
              </button>
              <button
                type="button"
                onClick={() => onGenerate?.([policy.id])}
                className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-indigo-600"
              >
                Generate
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Results screen block after critical gaps. */
export function GapResultPolicyRecommendations({
  recommendations = [],
  onViewPolicy,
  onGenerate,
}) {
  if (!recommendations.length) return null;

  return (
    <div className="mb-4">
      <GapPolicyMainPanel
        recommendations={recommendations}
        onViewPolicy={onViewPolicy}
        onGenerate={onGenerate}
      />
    </div>
  );
}
